/**
 * Early prefetch script that runs before main app bundle loads.
 * Fires API requests in parallel with app bundle loading.
 *
 * IMPORTANT: This file must remain dependency-free (no library imports except @core-ui/cookie-manager)
 * for performance reasons. It runs before the main bundle loads.
 */

import { getToken } from '@core-ui/cookie-manager';

interface OIDCUser {
  access_token: string;
  id_token: string;
  expires_at?: number;
}

// SYNC: This interface must match src/app/types/auth.ts::CheckTokenResponse
// Any changes to CheckTokenResponse shape must be reflected here.
interface CheckTokenResponse {
  email: string;
  isGuidesAdmin: boolean;
  disableSentry: boolean;
}

declare const SKIP_PREFETCH_PATHS: string[];

(function prefetchBootstrap() {
  try {
    const env = window._env_;
    if (!env || !env.ZITADEL_API_URL || !env.LEDGER_API_URL || !env.CATALOG_API_URL) {
      return;
    }

    // Skip prefetch on auth/redirect/special pages
    const path = window.location.pathname;
    const skipPaths = SKIP_PREFETCH_PATHS;
    for (let i = 0; i < skipPaths.length; i++) {
      if (path.startsWith(skipPaths[i])) {
        return;
      }
    }

    /**
     * Simple fetch wrapper that returns null on any error.
     * No retry logic - failures are handled by main app.
     */
    const safeFetch = <T>(url: string, init: RequestInit): Promise<T | null> => {
      // SSRF is not frontend issue, it's backend issue. semgrep just found it, but it's not our issue.
      // nosemgrep
      return fetch(url, init)
        .then((r) => (r.ok ? (r.json() as Promise<T>) : null))
        .catch(() => null);
    };

    let checkToken: Promise<CheckTokenResponse | null> | null = null;

    // --- OIDC flow ---
    // SYNC: OIDC key format must match OidcAuthStrategy.hasCredentials() (src/app/services/auth/OidcAuthStrategy.ts:58)
    if (env.CLIENT_ID) {
      const oidcKey = `oidc.user:${env.ZITADEL_API_URL}:${env.CLIENT_ID}`;
      try {
        const oidcUser: OIDCUser | null = JSON.parse(localStorage.getItem(oidcKey) ?? 'null');
        if (oidcUser?.access_token && oidcUser?.id_token) {
          const isExpired = oidcUser.expires_at && oidcUser.expires_at < Math.floor(Date.now() / 1000);
          if (!isExpired) {
            checkToken = safeFetch(`${env.ZITADEL_API_URL}/auth/login/check-token`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ access_token: oidcUser.access_token, id_token: oidcUser.id_token }),
            });
          }
        }
      } catch {
        // JSON parse error
      }
    }

    // --- Custom auth flow ---
    if (!checkToken) {
      // SYNC: cookie read mirrors TokenStore.getRefreshTokenValue() (src/app/services/auth/TokenStore.ts:28)
      const tokenCookie = getToken();
      // SYNC: key must match PREFETCH_EMAIL_KEY in src/app/sagas.ts and src/app/sagas/sagaHelpers.ts
      const cachedEmail = localStorage.getItem('__prefetch_email__');

      if (tokenCookie && cachedEmail && env.AUTH_API_URL) {
        checkToken = safeFetch(`${env.ZITADEL_API_URL}/auth/login/check-token`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cachedEmail }),
        });
      }
    }

    if (!checkToken) {
      return;
    }

    // Fire all data requests in parallel
    const tails = safeFetch<{ result: unknown[] }>(`${env.LEDGER_API_URL}/api/v2/get_tail_numbers`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });

    const currencies = safeFetch<unknown[]>(`${env.CATALOG_API_URL}/api/v1/currency_list`, {
      method: 'GET',
      credentials: 'include',
    });

    window.__prefetch__ = { checkToken, tails, currencies };
  } catch {
    // silent fail
  }
})();
